import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import React from 'react';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { CopyToClipboard } from 'src/components/CopyToClipboard';
import { FormLabel } from 'src/components/FormLabel';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { Typography, TypographyProps } from 'src/components/Typography';
import { formatFullDate } from 'src/utils/dateFormatter';

interface EditableFieldWrapperProps {
  value: any;
  label?: React.ReactNode;
  editLabel?: React.ReactNode;
  children?: React.ReactNode;
  editing: boolean;
  allowCopyToClipboard?: boolean;
  unit?: string;
  fullWidth?: boolean;
  valueVariant?: TypographyProps['variant'];
}

export const EditableFieldWrapper: React.FC<EditableFieldWrapperProps> = ({
  value,
  label,
  editLabel,
  editing,
  children,
  allowCopyToClipboard = false,
  unit,
  fullWidth = true,
  valueVariant = 'body1',
}) => {
  if (editing && editLabel) {
    label = editLabel;
  }

  const realValue = value;

  const valueIsElem = React.isValidElement(value);
  const valueIsText =
    typeof realValue === 'string' || typeof realValue === 'number';
  const valueIsArray = Array.isArray(realValue);
  const valueIsDate = realValue instanceof Date;
  const valueIsObj =
    !valueIsElem &&
    typeof realValue === 'object' &&
    realValue !== null &&
    !valueIsArray &&
    !valueIsDate;
  const keys = valueIsObj && Object.keys(realValue);
  return (
    <FormLabel
      sx={{ width: fullWidth ? '100%' : 'auto', whiteSpace: 'normal' }}
    >
      {label && (
        <Box mb={1} sx={{ color: 'text.secondary' }}>
          {label}
        </Box>
      )}
      {!editing &&
        (realValue || realValue === 0 ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: label ? 1.6 : 0, mb: 0 }}
          >
            {valueIsElem && (
              <Typography component="span">{realValue}</Typography>
            )}
            {valueIsText && (
              <Typography
                sx={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
                variant={valueVariant}
                component="span"
              >
                {realValue}
                {unit && unit}
              </Typography>
            )}
            {valueIsArray && (
              <Stack direction="row" spacing={0} sx={{ flexWrap: 'wrap' }}>
                {realValue.map((v, i) => (
                  <Chip key={v + ' ' + i} label={v} sx={{ mr: 2, mb: 1 }} />
                ))}
              </Stack>
            )}
            {valueIsDate && (
              <Typography sx={{ wordBreak: 'break-word' }}>
                {formatFullDate(realValue)}
              </Typography>
            )}
            {valueIsObj && (
              <Typography sx={{ wordBreak: 'break-word' }}>
                {(keys || []).map((key) => (
                  <React.Fragment key={key}>
                    <Typography
                      component="span"
                      color="text.secondary"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {key}:&nbsp;
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ mr: 2, wordBreak: 'break-word' }}
                    >
                      {realValue[key]}
                    </Typography>
                  </React.Fragment>
                ))}
              </Typography>
            )}
            {allowCopyToClipboard && (
              <CopyToClipboard text={realValue}>
                <IconButton size="small">
                  <Icon
                    icon={faCopy}
                    size="small"
                  />
                </IconButton>
              </CopyToClipboard>
            )}
          </Stack>
        ) : (
          <Typography
            color="text.secondary"
            sx={{ mt: 1.6, mb: 0, opacity: 0.3 }}
          >
            ———
          </Typography>
        ))}
      {editing && children}
    </FormLabel>
  );
};
